import { useEffect, useState } from "react";

interface FilmGalleryProps {
  stills: string[];
  title: string;
}

export default function FilmGallery({ stills, title }: FilmGalleryProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (openIndex === null) return;
      if (event.key === "Escape") setOpenIndex(null);
      if (event.key === "ArrowRight") setOpenIndex((prev) => (prev === null ? prev : (prev + 1) % stills.length));
      if (event.key === "ArrowLeft")
        setOpenIndex((prev) => (prev === null ? prev : (prev - 1 + stills.length) % stills.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex, stills.length]);

  useEffect(() => {
    document.body.style.overflow = openIndex !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [openIndex]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
        {stills.map((still, index) => (
          <button
            key={still}
            type="button"
            onClick={() => setOpenIndex(index)}
            className="group relative aspect-[16/9] overflow-hidden rounded-lg bg-secondary-100"
          >
            <img
              src={still}
              alt={`${title}剧照${index + 1}`}
              title={`${title} - 剧照 - 星光传媒`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-foreground-950/0 text-background-50 opacity-0 transition-all group-hover:bg-foreground-950/40 group-hover:opacity-100">
              <i className="ri-zoom-in-line text-2xl" />
            </span>
          </button>
        ))}
      </div>

      {openIndex !== null && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-foreground-950/90 p-4"
          onClick={() => setOpenIndex(null)}
        >
          <button
            type="button"
            aria-label="关闭预览"
            onClick={() => setOpenIndex(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:right-6 md:top-6"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <button
            type="button"
            aria-label="上一张"
            onClick={(event) => {
              event.stopPropagation();
              setOpenIndex((prev) => (prev === null ? prev : (prev - 1 + stills.length) % stills.length));
            }}
            className="absolute left-3 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:left-6"
          >
            <i className="ri-arrow-left-s-line text-2xl" />
          </button>
          <img
            src={stills[openIndex]}
            alt={`${title}剧照${openIndex + 1}`}
            className="max-h-[85vh] max-w-full animate-zoom-in rounded-lg object-contain"
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            aria-label="下一张"
            onClick={(event) => {
              event.stopPropagation();
              setOpenIndex((prev) => (prev === null ? prev : (prev + 1) % stills.length));
            }}
            className="absolute right-3 flex h-10 w-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 transition-colors hover:bg-background-50/30 md:right-6"
          >
            <i className="ri-arrow-right-s-line text-2xl" />
          </button>
          <span className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-background-50/15 px-3 py-1 text-sm text-background-100">
            {openIndex + 1} / {stills.length}
          </span>
        </div>
      )}
    </>
  );
}